import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import QrScanner from 'qr-scanner';
import api from "../api";
import { ReactComponent as CloseIcon } from "../assets/images/icons/ic-close.svg";
import { ReactComponent as QrCodeIcon } from "../assets/images/icons/ic-qrcode.svg";
import MetricStatBlock from "../components/analytics/MetricStatBlock";
import StatusBadge from "../components/StatusBadge";
import Button from '../components/Button';
import Card from "../components/cards/Card";
import AlertDialog from "../components/dialogs/AlertDialog";
import Dialog from "../components/dialogs/Dialog";
import HeaderFooterLayout from '../components/layouts/HeaderFooterLayout';
import Pagination from '../components/Pagination';
import Table from "../components/Table";
import TextInput from "../components/TextInput";
import MainContext from "../common/MainContext";
import { formatDateV2, formatNumber, formatTimeV2 } from "../utils";
import useWindowDimensions from '../common/hooks/useWindowDimensions';
import baseStyles from './Event.module.css';
import managerStyles from './EventManager.module.css';
import Breadcrumb from '../components/Breadcrumb';
const styles = { ...baseStyles, ...Object.fromEntries(Object.entries(managerStyles).map(([key, value]) => [key, `${baseStyles[key] || ''} ${value}`])) };

const SCAN_SUCCESS_RESUME_DELAY_MS = 2000;
const SCAN_ERROR_RESUME_DELAY_MS = 900;
const DUPLICATE_SCAN_GUARD_MS = 1200;
const OFFLINE_TICKETS_FULL_SYNC_PER_PAGE = 1000;
const OFFLINE_TICKETS_INCREMENTAL_PER_PAGE = 100;
const OFFLINE_SCAN_SYNC_BATCH_SIZE = 25;
const OFFLINE_SCAN_SYNC_INTERVAL_MS = 12000;

const buildOfflineTicketsStorageKey = (eventId) => `event-manager-offline-tickets:${eventId}`;
const buildOfflineTicketsMetadataStorageKey = (eventId) => `event-manager-offline-tickets-metadata:${eventId}`;
const buildPendingScansStorageKey = (eventId) => `event-manager-offline-pending-scans:${eventId}`;

const sanitizeScanEntry = (scan) => ({
    admin_id: scan?.admin_id ?? null,
    admin_name: typeof scan?.admin_name === 'string' ? scan.admin_name : '',
    scanned_at: typeof scan?.scanned_at === 'string' ? scan.scanned_at : null,
});

const normalizeTicketForOfflineMap = (ticket) => {
    const ticketId = ticket?.ticket_id ?? ticket?.id;
    if (!ticketId) {
        return null;
    }

    return {
        ticket_id: ticketId,
        user: {
            name: ticket?.user?.name || '',
            surname: ticket?.user?.surname || '',
        },
        type: {
            id: ticket?.type?.id ?? null,
            name: ticket?.type?.name || '',
        },
        scans: (Array.isArray(ticket?.scans) ? ticket.scans : []).map(sanitizeScanEntry),
        activity_bookings: Array.isArray(ticket?.activity_bookings) ? ticket.activity_bookings : [],
    };
};

const buildScanableTicketFromOfflineEntry = (qrToken, offlineEntry) => {
    if (!offlineEntry?.ticket_id) {
        return null;
    }

    const scans = Array.isArray(offlineEntry.scans) ? offlineEntry.scans : [];
    return {
        id: offlineEntry.ticket_id,
        ticket_id: offlineEntry.ticket_id,
        qr_token: qrToken,
        user: {
            name: offlineEntry?.user?.name || '',
            surname: offlineEntry?.user?.surname || '',
        },
        type: {
            id: offlineEntry?.type?.id ?? null,
            name: offlineEntry?.type?.name || '',
        },
        scans,
        activity_bookings: Array.isArray(offlineEntry?.activity_bookings) ? offlineEntry.activity_bookings : [],
        status: scans.length > 0 ? 'used' : 'active',
    };
};

const buildOfflineTicketsMapFromApiTickets = (tickets) => {
    const map = new Map();

    (Array.isArray(tickets) ? tickets : []).forEach((ticket) => {
        const qrToken = typeof ticket?.qr_token === 'string' ? ticket.qr_token.trim() : '';
        if (!qrToken) {
            return;
        }

        const normalized = normalizeTicketForOfflineMap(ticket);
        if (!normalized) {
            return;
        }

        map.set(qrToken, normalized);
    });

    return map;
};

const readOfflineTicketsMapFromStorage = (eventId) => {
    if (!eventId) {
        return new Map();
    }

    try {
        const raw = localStorage.getItem(buildOfflineTicketsStorageKey(eventId));
        if (!raw) {
            return new Map();
        }

        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
            return new Map();
        }

        return new Map(parsed);
    } catch (error) {
        console.error('Impossibile leggere la cache offline dei ticket', error);
        return new Map();
    }
};

const persistOfflineTicketsMapToStorage = (eventId, map) => {
    if (!eventId) {
        return;
    }

    try {
        localStorage.setItem(buildOfflineTicketsStorageKey(eventId), JSON.stringify(Array.from(map.entries())));
        return true;
    } catch (error) {
        console.error('Impossibile salvare la cache offline dei ticket', error);
        return false;
    }
};

const getLastOfflineTicketId = (map) => {
    let lastTicketId = 0;
    if (!(map instanceof Map)) {
        return lastTicketId;
    }

    map.forEach((ticket) => {
        const ticketId = Number(ticket?.ticket_id || 0);
        if (Number.isFinite(ticketId) && ticketId > lastTicketId) {
            lastTicketId = ticketId;
        }
    });

    return lastTicketId;
};

const getLastApiTicketId = (tickets) => (
    (Array.isArray(tickets) ? tickets : []).reduce((lastTicketId, ticket) => {
        const ticketId = Number(ticket?.ticket_id ?? ticket?.id ?? 0);
        return Number.isFinite(ticketId) && ticketId > lastTicketId ? ticketId : lastTicketId;
    }, 0)
);

const readOfflineTicketsMetadataFromStorage = (eventId) => {
    if (!eventId) {
        return { lastTicketId: 0, fullSyncCompleted: false };
    }

    try {
        const raw = localStorage.getItem(buildOfflineTicketsMetadataStorageKey(eventId));
        if (!raw) {
            return { lastTicketId: 0, fullSyncCompleted: false };
        }

        const parsed = JSON.parse(raw);
        return {
            lastTicketId: Number(parsed?.lastTicketId || 0),
            fullSyncCompleted: parsed?.fullSyncCompleted === true,
        };
    } catch (error) {
        console.error('Impossibile leggere i metadata della cache offline dei ticket', error);
        return { lastTicketId: 0, fullSyncCompleted: false };
    }
};

const persistOfflineTicketsMetadataToStorage = (eventId, metadata) => {
    if (!eventId) {
        return;
    }

    try {
        localStorage.setItem(buildOfflineTicketsMetadataStorageKey(eventId), JSON.stringify({
            lastTicketId: Number(metadata?.lastTicketId || 0),
            fullSyncCompleted: metadata?.fullSyncCompleted === true,
        }));
        return true;
    } catch (error) {
        console.error('Impossibile salvare i metadata della cache offline dei ticket', error);
        return false;
    }
};

const mergeOfflineTicketMaps = (baseMap, fetchedMap) => {
    const mergedMap = new Map(baseMap instanceof Map ? baseMap : []);

    fetchedMap.forEach((fetchedTicket, qrToken) => {
        const previousTicket = mergedMap.get(qrToken);
        const previousScans = Array.isArray(previousTicket?.scans) ? previousTicket.scans : [];
        const fetchedScans = Array.isArray(fetchedTicket?.scans) ? fetchedTicket.scans : [];

        mergedMap.set(qrToken, {
            ...fetchedTicket,
            scans: previousScans.length > fetchedScans.length ? previousScans : fetchedScans,
        });
    });

    return mergedMap;
};

const readPendingScansFromStorage = (eventId) => {
    if (!eventId) {
        return [];
    }

    try {
        const raw = localStorage.getItem(buildPendingScansStorageKey(eventId));
        if (!raw) {
            return [];
        }

        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        console.error('Impossibile leggere la coda scansioni offline', error);
        return [];
    }
};

const persistPendingScansToStorage = (eventId, pendingScans) => {
    if (!eventId) {
        return;
    }

    try {
        localStorage.setItem(buildPendingScansStorageKey(eventId), JSON.stringify(pendingScans));
    } catch (error) {
        console.error('Impossibile salvare la coda scansioni offline', error);
    }
};

const EventManager = () => {
    const { width } = useWindowDimensions();
    const { t } = useTranslation();
    const context = useContext(MainContext);
    const { id } = useParams();
    const eventId = id;

    const [event, setEvent] = useState(null);
    const [loadingEvent, setLoadingEvent] = useState(false);

    const [loadingTickets, setLoadingTickets] = useState(false);
    const [tickets, setTickets] = useState([]);
    const [loadingOfflineTicketsPreload, setLoadingOfflineTicketsPreload] = useState(false);
    const [offlineDownload, setOfflineDownload] = useState({ status: 'idle', downloaded: 0, total: null });
    const [offlineTicketsByQrToken, setOfflineTicketsByQrToken] = useState(new Map());
    const [pendingScanSyncQueue, setPendingScanSyncQueue] = useState([]);
    const [syncingPendingScans, setSyncingPendingScans] = useState(false);
    const [filteredTotal, setFilteredTotal] = useState(0);
    const [soldTotal, setSoldTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [per] = useState(25);
    const [q, setQ] = useState('');

    const [scannedTotal, setScannedTotal] = useState(0);
    const [loadingScannedTotal, setLoadingScannedTotal] = useState(false);

    const [qrScannerOpen, setQrScannerOpen] = useState(false);
    const [startingQrScanner, setStartingQrScanner] = useState(false);
    const [scannerPaused, setScannerPaused] = useState(false);
    const [scannerError, setScannerError] = useState('');
    const [scannerSuccess, setScannerSuccess] = useState('');
    const [videoInputDevices, setVideoInputDevices] = useState([]);
    const [selectedCameraId, setSelectedCameraId] = useState('');
    const [lastScannedCode, setLastScannedCode] = useState('');
    const [scanningTicket, setScanningTicket] = useState(false);
    const [scannedTicket, setScannedTicket] = useState(null);

    const [selectedTicket, setSelectedTicket] = useState(null);
    const [manualScanLoading, setManualScanLoading] = useState(false);
    const [manualScanError, setManualScanError] = useState('');
    const [manualScanSuccess, setManualScanSuccess] = useState('');
    const [manualScanConfirm, setManualScanConfirm] = useState({ open: false, code: null });

    const qrVideoRef = useRef(null);
    const qrScannerRef = useRef(null);
    const qrAutoResumeTimeoutRef = useRef(null);
    const scanningTicketRef = useRef(false);
    const offlineTicketsByQrTokenRef = useRef(new Map());
    const offlineTicketsMetadataRef = useRef({ lastTicketId: 0, fullSyncCompleted: false });
    const pendingScanSyncQueueRef = useRef([]);
    const syncingPendingScansRef = useRef(false);
    const lastAcceptedScanRef = useRef({ code: '', at: 0 });

    const getLatestScan = useCallback((ticket) => {
        const scans = Array.isArray(ticket?.scans) ? ticket.scans : [];
        if (scans.length === 0) {
            return null;
        }

        const sortedScans = [...scans].sort((a, b) => {
            const aTime = a?.scanned_at ? new Date(a.scanned_at).getTime() : 0;
            const bTime = b?.scanned_at ? new Date(b.scanned_at).getTime() : 0;
            return bTime - aTime;
        });

        return sortedScans[0] || null;
    }, []);

    const getTicketActivityBookings = useCallback((ticket) => {
        const bookings = Array.isArray(ticket?.activity_bookings) ? ticket.activity_bookings : [];
        return [...bookings].sort((a, b) => {
            const aTime = a?.slot?.starts_at ? new Date(a.slot.starts_at).getTime() : Number.MAX_SAFE_INTEGER;
            const bTime = b?.slot?.starts_at ? new Date(b.slot.starts_at).getTime() : Number.MAX_SAFE_INTEGER;
            return aTime - bTime;
        });
    }, []);

    const getActivityBookingTimeRange = useCallback((booking) => {
        const startsAt = booking?.slot?.starts_at;
        const endsAt = booking?.slot?.ends_at;

        if (!startsAt || !endsAt) {
            return 'Orario non disponibile';
        }

        return `${formatTimeV2(startsAt)} - ${formatTimeV2(endsAt)}`;
    }, []);

    const getActivityBookingMeta = useCallback((booking) => {
        const activityName = booking?.activity?.name || 'Attivita';
        const slotName = booking?.slot?.name || 'Slot';

        const normalizePeople = (value) => {
            if (!value) {
                return [];
            }

            if (Array.isArray(value)) {
                return value
                    .map((item) => {
                        if (typeof item === 'string') {
                            return item.trim();
                        }

                        if (item && typeof item === 'object') {
                            const fullName = `${item.name || ''} ${item.surname || ''}`.trim();
                            return fullName || item.full_name || item.label || '';
                        }

                        return '';
                    })
                    .filter((name) => !!name);
            }

            if (typeof value === 'string') {
                return value
                    .split(',')
                    .map((item) => item.trim())
                    .filter((name) => !!name);
            }

            return [];
        };

        const guests = normalizePeople(
            booking?.slot?.guests
            || booking?.slot?.guest_names
            || booking?.slot?.guest_list
        );

        const moderators = normalizePeople(
            booking?.slot?.moderators
            || booking?.slot?.moderator_names
            || booking?.slot?.moderator_list
        );

        return (
            <>
                <div>{`${activityName} • ${slotName}`}</div>
                {guests.length > 0 && (
                    <div className={styles.ticketActivityPeopleSection}>
                        <div className={styles.ticketActivityPeopleLabel}>Ospiti</div>
                        <ul className={styles.ticketActivityPeopleList}>
                            {guests.map((guestName, index) => (
                                <li className={styles.ticketActivityPeopleItem} key={`guest-${booking?.id || slotName}-${index}`}>
                                    {guestName}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {moderators.length > 0 && (
                    <div className={styles.ticketActivityPeopleSection}>
                        <div className={styles.ticketActivityPeopleLabel}>Moderatori</div>
                        <ul className={styles.ticketActivityPeopleList}>
                            {moderators.map((moderatorName, index) => (
                                <li className={styles.ticketActivityPeopleItem} key={`moderator-${booking?.id || slotName}-${index}`}>
                                    {moderatorName}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </>
        );
    }, []);

    const getTicketStatusBadge = (status) => {
        const statusColors = {
            active: 'var(--sf-green)',
            used: 'var(--sf-blue)',
            pending: '#a16207',
            expired: 'var(--sf-red)',
            revoked: '#64748b',
        };

        return (
            <StatusBadge
                appearance="soft"
                label={Object.prototype.hasOwnProperty.call(statusColors, status)
                    ? t(`events.tickets.types.${status}`)
                    : status || '—'}
                color={statusColors[status] || '#64748b'}
            />
        );
    };

    const translateScanErrorMessage = useCallback((message) => {
        const normalizedMessage = typeof message === 'string' ? message.trim() : '';
        const normalizedKey = normalizedMessage.toLowerCase().replace(/\.+$/, '');

        const errorMap = {
            'ticket not found': 'events.errors.ticketNotFound',
            'invalid ticket code': 'events.errors.invalidTicketCode',
            'ticket is expired': 'events.errors.ticketExpired',
            'ticket has been revoked': 'events.errors.ticketRevoked',
            'ticket has not been paid': 'events.errors.ticketNotPaid',
            'ticket does not belong to selected event': 'events.errors.ticketWrongEvent',
            'ticket can be scanned only between the event start day and end day': 'events.errors.scanOutsideEventDays',
            'generic error': 'events.errors.generic',
        };

        const translationKey = errorMap[normalizedKey];
        if (translationKey) {
            return t(translationKey);
        }

        return normalizedMessage || t('events.errors.generic');
    }, [t]);

    const getEvent = useCallback(async () => {
        if (!eventId) {
            return;
        }

        setLoadingEvent(true);
        try {
            const response = await api.get(`/admin/events/${eventId}`);
            setEvent(response);
        } catch (error) {
            console.error(error);
        }
        setLoadingEvent(false);
    }, [eventId]);

    const getTickets = useCallback(async () => {
        if (!eventId) {
            return;
        }

        setLoadingTickets(true);
        try {
            let path = `/admin/events/${eventId}/tickets?page=${page}&per=${per}`;
            if (q && q.trim() !== '') {
                path += `&q=${encodeURIComponent(q.replaceAll(' ', '').trim().toLowerCase())}`;
            }

            const response = await api.get(path);
            setTickets(response.tickets || []);
            const nextTotal = Number(response.total || 0);
            setFilteredTotal(nextTotal);

            if (!q || q.trim() === '') {
                setSoldTotal(nextTotal);
            }
        } catch (error) {
            console.error(error);
        }
        setLoadingTickets(false);
    }, [eventId, page, per, q]);

    const getScannedTicketsTotal = useCallback(async () => {
        if (!eventId) {
            return;
        }

        setLoadingScannedTotal(true);
        try {
            const response = await api.get(`/admin/events/${eventId}/tickets/scanned_count`);
            setScannedTotal(Number(response?.scanned || 0));
        } catch (error) {
            console.error(error);
        }
        setLoadingScannedTotal(false);
    }, [eventId]);

    const updateOfflineTicketMap = useCallback((nextMap) => {
        offlineTicketsByQrTokenRef.current = nextMap;
        setOfflineTicketsByQrToken(nextMap);
        return persistOfflineTicketsMapToStorage(eventId, nextMap);
    }, [eventId]);

    const updateOfflineTicketsMetadata = useCallback((nextMetadata) => {
        const saved = persistOfflineTicketsMetadataToStorage(eventId, nextMetadata);
        if (saved) offlineTicketsMetadataRef.current = nextMetadata;
        return saved;
    }, [eventId]);

    const updatePendingScanSyncQueue = useCallback((nextQueue) => {
        pendingScanSyncQueueRef.current = nextQueue;
        setPendingScanSyncQueue(nextQueue);
        persistPendingScansToStorage(eventId, nextQueue);
    }, [eventId]);

    const enqueuePendingScan = useCallback(({ qrToken, scannedAt }) => {
        const pendingItem = {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            qr_token: qrToken,
            scanned_at: scannedAt || null,
            queued_at: new Date().toISOString(),
        };

        updatePendingScanSyncQueue([...pendingScanSyncQueueRef.current, pendingItem]);
    }, [updatePendingScanSyncQueue]);

    const syncTicketScansBatchToServer = useCallback(async (scanBatch) => {
        const payload = {
            scans: (Array.isArray(scanBatch) ? scanBatch : []).map((item) => ({
                qr_token: item?.qr_token,
                scanned_at: item?.scanned_at || null,
            })),
        };

        return await api.post(`/admin/events/${eventId}/tickets/scans/batch`, payload);
    }, [eventId]);

    const preloadOfflineTicketsMap = useCallback(async () => {
        if (!eventId) {
            return;
        }

        try {
            const metadata = offlineTicketsMetadataRef.current;
            const shouldRunFullSync = metadata.fullSyncCompleted !== true;
            const per = shouldRunFullSync
                ? OFFLINE_TICKETS_FULL_SYNC_PER_PAGE
                : OFFLINE_TICKETS_INCREMENTAL_PER_PAGE;

            setLoadingOfflineTicketsPreload(true);
            setOfflineDownload({ status: 'loading', downloaded: shouldRunFullSync ? 0 : offlineTicketsByQrTokenRef.current.size, total: null });
            let downloaded = shouldRunFullSync ? 0 : offlineTicketsByQrTokenRef.current.size;
            let downloadTotal = null;

            let currentMap = new Map(offlineTicketsByQrTokenRef.current);
            let lastTicketId = shouldRunFullSync ? 0 : Math.max(
                Number(metadata.lastTicketId || 0),
                getLastOfflineTicketId(offlineTicketsByQrTokenRef.current)
            );
            let hasMoreTickets = true;

            while (hasMoreTickets) {
                const response = await api.get(
                    `/admin/events/${eventId}/tickets?per=${per}&after_id=${lastTicketId}`,
                    {
                        // Full sync can exceed default timeout on slower networks.
                        timeout: shouldRunFullSync ? 0 : undefined,
                    }
                );

                const fetchedTickets = Array.isArray(response?.tickets) ? response.tickets : [];
                const fetchedMap = buildOfflineTicketsMapFromApiTickets(fetchedTickets);
                currentMap = mergeOfflineTicketMaps(currentMap, fetchedMap);
                const nextLastTicketId = Math.max(lastTicketId, getLastApiTicketId(fetchedTickets));
                if (fetchedTickets.length > 0 && nextLastTicketId <= lastTicketId) {
                    throw new Error('Il download dei biglietti non sta avanzando.');
                }
                lastTicketId = nextLastTicketId;

                if (!updateOfflineTicketMap(currentMap)) {
                    throw new Error('Impossibile salvare i biglietti sul dispositivo. Lo spazio disponibile potrebbe essere esaurito.');
                }
                if (!updateOfflineTicketsMetadata({
                    lastTicketId,
                    fullSyncCompleted: shouldRunFullSync ? fetchedTickets.length < per : true,
                })) {
                    throw new Error('Impossibile salvare lo stato del download sul dispositivo.');
                }
                // The API total counts tickets remaining after the requested cursor.
                const remaining = Number(response?.total);
                if (Number.isFinite(remaining) && remaining >= 0) {
                    downloadTotal = Math.max(downloadTotal || 0, downloaded + remaining);
                }
                downloaded += fetchedTickets.length;
                setOfflineDownload({ status: 'loading', downloaded, total: downloadTotal });

                hasMoreTickets = fetchedTickets.length === per;
            }
            setOfflineDownload({ status: 'success', downloaded: currentMap.size, total: currentMap.size });
        } catch (error) {
            setOfflineDownload(previous => ({ ...previous, status: 'error', message: error?.message || 'Controlla la connessione e riprova.' }));
            console.error('Impossibile precaricare i ticket offline', error);
        } finally {
            setLoadingOfflineTicketsPreload(false);
        }
    }, [eventId, updateOfflineTicketMap, updateOfflineTicketsMetadata]);

    const selectedTicketDetails = useMemo(() => {
        if (!selectedTicket) {
            return null;
        }

        const ticketFromList = tickets.find((ticket) => ticket.id === selectedTicket.id);
        return ticketFromList || selectedTicket;
    }, [selectedTicket, tickets]);

    const scannedTicketDetails = useMemo(() => {
        if (!scannedTicket) {
            return null;
        }

        const ticketFromList = tickets.find((ticket) => ticket.id === scannedTicket.id);
        return ticketFromList || scannedTicket;
    }, [scannedTicket, tickets]);

    const clearQrAutoResumeTimeout = useCallback(() => {
        if (qrAutoResumeTimeoutRef.current) {
            clearTimeout(qrAutoResumeTimeoutRef.current);
            qrAutoResumeTimeoutRef.current = null;
        }
    }, []);

    const stopQrScanner = useCallback(() => {
        if (qrScannerRef.current) {
            qrScannerRef.current.stop();
            qrScannerRef.current.destroy();
            qrScannerRef.current = null;
        }

        if (qrVideoRef.current) {
            qrVideoRef.current.pause();
            qrVideoRef.current.srcObject = null;
        }
    }, []);

    const loadVideoInputDevices = useCallback(async () => {
        try {
            const cameras = await QrScanner.listCameras(true);
            const videoDevices = cameras.map((camera) => ({
                deviceId: camera.id,
                label: camera.label || '',
            }));

            setVideoInputDevices(videoDevices);
            setSelectedCameraId((prevSelected) => {
                if (prevSelected && videoDevices.some((device) => device.deviceId === prevSelected)) {
                    return prevSelected;
                }

                const rearCamera = videoDevices.find((device) => {
                    const label = (device.label || '').toLowerCase();
                    return label.includes('back') || label.includes('rear') || label.includes('environment') || label.includes('posteriore');
                });

                return rearCamera?.deviceId || videoDevices[0]?.deviceId || '';
            });
        } catch (error) {
            console.error('Impossibile ottenere la lista delle fotocamere', error);
        }
    }, []);

    const performTicketScan = useCallback(async (code) => {
        try {
            const ticket = await api.get(`/admin/tickets/scan/${encodeURIComponent(code)}?event_id=${eventId}`);
            return ticket;
        } catch (error) {
            const responseData = error?.response?.data || error;
            const responseDetail = responseData?.detail;

            let detailMessage = null;

            if (typeof responseDetail === 'string' && responseDetail.trim() !== '') {
                detailMessage = responseDetail;
            } else if (responseDetail && typeof responseDetail === 'object') {
                if (typeof responseDetail.detail === 'string' && responseDetail.detail.trim() !== '') {
                    detailMessage = responseDetail.detail;
                } else if (typeof responseDetail.message === 'string' && responseDetail.message.trim() !== '') {
                    detailMessage = responseDetail.message;
                }
            }

            if (!detailMessage && typeof responseData?.message === 'string' && responseData.message.trim() !== '') {
                detailMessage = responseData.message;
            }

            if (!detailMessage && typeof error?.detail === 'string' && error.detail.trim() !== '') {
                detailMessage = error.detail;
            } else if (!detailMessage && error?.detail && typeof error.detail === 'object') {
                if (typeof error.detail.detail === 'string' && error.detail.detail.trim() !== '') {
                    detailMessage = error.detail.detail;
                } else if (typeof error.detail.message === 'string' && error.detail.message.trim() !== '') {
                    detailMessage = error.detail.message;
                }
            }

            if (!detailMessage && typeof error?.message === 'string' && error.message.trim() !== '') {
                detailMessage = error.message;
            }

            throw new Error(translateScanErrorMessage(detailMessage));
        }
    }, [eventId, translateScanErrorMessage]);

    const mergeScannedTicketInState = useCallback((ticket) => {
        if (!ticket?.id) {
            return;
        }

        setTickets((previousTickets) => {
            if (!Array.isArray(previousTickets) || previousTickets.length === 0) {
                return previousTickets;
            }

            let updated = false;
            const nextTickets = previousTickets.map((currentTicket) => {
                if (currentTicket.id !== ticket.id) {
                    return currentTicket;
                }

                updated = true;
                return {
                    ...currentTicket,
                    ...ticket,
                };
            });

            return updated ? nextTickets : previousTickets;
        });

        setSelectedTicket((previousSelectedTicket) => {
            if (!previousSelectedTicket || previousSelectedTicket.id !== ticket.id) {
                return previousSelectedTicket;
            }

            return {
                ...previousSelectedTicket,
                ...ticket,
            };
        });
    }, []);

    const flushPendingScans = useCallback(async () => {
        if (!eventId || syncingPendingScansRef.current) {
            return;
        }

        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            return;
        }

        const currentQueue = pendingScanSyncQueueRef.current;
        if (!Array.isArray(currentQueue) || currentQueue.length === 0) {
            return;
        }

        syncingPendingScansRef.current = true;
        setSyncingPendingScans(true);

        let remainingQueue = [...currentQueue];
        const currentBatch = remainingQueue.slice(0, OFFLINE_SCAN_SYNC_BATCH_SIZE);
        remainingQueue = remainingQueue.slice(currentBatch.length);

        try {
            const batchResult = await syncTicketScansBatchToServer(currentBatch);
            const failedItems = Array.isArray(batchResult?.failed) ? batchResult.failed : [];

            const failedKeys = new Set(
                failedItems
                    .map((item) => `${String(item?.qr_token || '')}|${String(item?.scanned_at || '')}`)
            );

            if (failedItems.length > 0) {
                failedItems.forEach((item) => {
                    console.error('Scansione batch non sincronizzata, verra ritentata', item?.qr_token, item?.detail || 'Generic error');
                });
            }

            const failedFromBatch = currentBatch.filter((item) => {
                const key = `${String(item?.qr_token || '')}|${String(item?.scanned_at || '')}`;
                return failedKeys.has(key);
            });

            remainingQueue = [...failedFromBatch, ...remainingQueue];
        } catch (error) {
            console.error('Impossibile sincronizzare il batch scansioni offline, batch mantenuto in coda', error);
            remainingQueue = [...currentBatch, ...remainingQueue];
        }

        updatePendingScanSyncQueue(remainingQueue);
        syncingPendingScansRef.current = false;
        setSyncingPendingScans(false);
    }, [eventId, syncTicketScansBatchToServer, updatePendingScanSyncQueue]);

    const scanTicketCode = useCallback(async (code) => {
        if (!code || scanningTicketRef.current) {
            return;
        }

        const now = Date.now();
        const isDuplicateBurst =
            lastAcceptedScanRef.current.code === code
            && (now - lastAcceptedScanRef.current.at) < DUPLICATE_SCAN_GUARD_MS;

        if (isDuplicateBurst) {
            return;
        }

        lastAcceptedScanRef.current = { code, at: now };

        const previousTicket =
            tickets.find((ticketItem) => ticketItem.qr_token === code)
            || (selectedTicketDetails?.qr_token === code ? selectedTicketDetails : null)
            || (scannedTicketDetails?.qr_token === code ? scannedTicketDetails : null);
        const wasAlreadyUsed = previousTicket?.status === 'used';

        scanningTicketRef.current = true;
        setScanningTicket(true);
        setScannerError('');
        setScannerSuccess('');
        clearQrAutoResumeTimeout();

        try {
            let ticket = null;
            const offlineTicket = offlineTicketsByQrToken.get(code);

            if (offlineTicket) {
                const adminName = [context?.user?.name, context?.user?.surname]
                    .map((value) => (typeof value === 'string' ? value.trim() : ''))
                    .filter(Boolean)
                    .join(' ');

                const newScan = {
                    admin_id: context?.user?.id ?? null,
                    admin_name: adminName || context?.user?.email || 'Admin',
                    scanned_at: new Date().toISOString(),
                };

                const updatedOfflineTicket = {
                    ...offlineTicket,
                    scans: [...(Array.isArray(offlineTicket.scans) ? offlineTicket.scans : []), newScan],
                };

                const nextMap = new Map(offlineTicketsByQrToken);
                nextMap.set(code, updatedOfflineTicket);
                updateOfflineTicketMap(nextMap);
                enqueuePendingScan({
                    qrToken: code,
                    scannedAt: newScan.scanned_at,
                });

                ticket = buildScanableTicketFromOfflineEntry(code, updatedOfflineTicket);
                if (!ticket) {
                    throw new Error(t('events.errors.invalidTicketCode'));
                }

                void flushPendingScans();
            } else {
                ticket = await performTicketScan(code);
                const normalizedRemoteTicket = normalizeTicketForOfflineMap(ticket);
                if (normalizedRemoteTicket) {
                    const nextMap = new Map(offlineTicketsByQrToken);
                    nextMap.set(code, normalizedRemoteTicket);
                    updateOfflineTicketMap(nextMap);
                }
            }

            setLastScannedCode(code);
            setScannedTicket(ticket);
            mergeScannedTicketInState(ticket);
            setScannerSuccess('Biglietto scansionato con successo');
            setScannerPaused(true);

            if (!wasAlreadyUsed && ticket?.status === 'used') {
                setScannedTotal((previousTotal) => previousTotal + 1);
            }

            qrAutoResumeTimeoutRef.current = setTimeout(() => {
                setScannerSuccess('');
                setScannerPaused(false);
                qrAutoResumeTimeoutRef.current = null;
            }, SCAN_SUCCESS_RESUME_DELAY_MS);
        } catch (error) {
            setScannerError(error.message || 'Errore durante la scansione del ticket');
            setScannerPaused(true);
            qrAutoResumeTimeoutRef.current = setTimeout(() => {
                setScannerError('');
                setScannerPaused(false);
                qrAutoResumeTimeoutRef.current = null;
            }, SCAN_ERROR_RESUME_DELAY_MS);
        }

        setScanningTicket(false);
        scanningTicketRef.current = false;
    }, [clearQrAutoResumeTimeout, context?.user?.email, context?.user?.id, context?.user?.name, context?.user?.surname, enqueuePendingScan, flushPendingScans, mergeScannedTicketInState, offlineTicketsByQrToken, performTicketScan, scannedTicketDetails, selectedTicketDetails, t, tickets, updateOfflineTicketMap]);

    const runManualTicketScan = useCallback(async () => {
        if (!selectedTicketDetails?.qr_token || manualScanLoading) {
            return;
        }

        setManualScanLoading(true);
        setManualScanError('');
        setManualScanSuccess('');

        try {
            const scanned = await performTicketScan(selectedTicketDetails.qr_token);
            setManualScanSuccess('Biglietto impostato come scansionato');
            const wasAlreadyUsed = selectedTicketDetails?.status === 'used';
            setSelectedTicket((prev) => ({ ...(prev || {}), ...scanned }));
            setScannedTicket(scanned);
            mergeScannedTicketInState(scanned);
            setLastScannedCode(selectedTicketDetails.qr_token);

            if (!wasAlreadyUsed && scanned?.status === 'used') {
                setScannedTotal((previousTotal) => previousTotal + 1);
            }

            void getScannedTicketsTotal();
        } catch (error) {
            setManualScanError(error.message || 'Errore durante la scansione del ticket');
        }

        setManualScanLoading(false);
    }, [getScannedTicketsTotal, manualScanLoading, mergeScannedTicketInState, performTicketScan, selectedTicketDetails]);

    const markTicketAsScannedNow = useCallback(() => {
        if (!selectedTicketDetails?.qr_token || manualScanLoading) {
            return;
        }

        setManualScanConfirm({
            open: true,
            code: selectedTicketDetails.qr_token,
        });
    }, [manualScanLoading, selectedTicketDetails?.qr_token]);

    const startQrScanner = useCallback(async () => {
        if (!qrScannerOpen || scannerPaused || !qrVideoRef.current) {
            return;
        }

        setStartingQrScanner(true);
        setScannerError('');

        try {
            const preferredCamera = selectedCameraId || 'environment';

            qrScannerRef.current = new QrScanner(
                qrVideoRef.current,
                async (scanResult) => {
                    const qrCode = typeof scanResult === 'string' ? scanResult : scanResult?.data;
                    if (!qrCode || scanningTicketRef.current || scannerPaused) {
                        return;
                    }

                    await scanTicketCode(qrCode);
                },
                {
                    preferredCamera,
                    maxScansPerSecond: 5,
                    returnDetailedScanResult: true,
                }
            );

            await qrScannerRef.current.start();
            await loadVideoInputDevices();
        } catch (error) {
            const message = typeof error?.message === 'string' ? error.message : '';
            const isPlayInterruptedError =
                error?.name === 'AbortError'
                || message.toLowerCase().includes('play() request was interrupted by a new load request');

            if (isPlayInterruptedError) {
                return;
            }

            const detail = error?.message || 'Impossibile avviare la fotocamera';
            setScannerError(detail);
            stopQrScanner();
        }

        setStartingQrScanner(false);
    }, [loadVideoInputDevices, qrScannerOpen, scanTicketCode, scannerPaused, selectedCameraId, stopQrScanner]);

    const openQrScanner = useCallback(() => {
        setScannedTicket(null);
        setLastScannedCode('');
        setScannerError('');
        setScannerSuccess('');
        setScannerPaused(false);
        lastAcceptedScanRef.current = { code: '', at: 0 };
        setQrScannerOpen(true);
        loadVideoInputDevices();
    }, [loadVideoInputDevices]);

    useEffect(() => {
        getEvent();
    }, [getEvent]);

    useEffect(() => {
        getTickets();
    }, [getTickets]);

    useEffect(() => {
        getScannedTicketsTotal();
    }, [getScannedTicketsTotal]);

    useEffect(() => {
        if (!eventId) {
            return;
        }

        const localMap = readOfflineTicketsMapFromStorage(eventId);
        const localMetadata = readOfflineTicketsMetadataFromStorage(eventId);
        const localLastTicketId = Math.max(
            Number(localMetadata.lastTicketId || 0),
            getLastOfflineTicketId(localMap)
        );
        offlineTicketsMetadataRef.current = {
            lastTicketId: localLastTicketId,
            fullSyncCompleted: localMetadata.fullSyncCompleted === true,
        };

        if (localMap.size > 0) {
            offlineTicketsByQrTokenRef.current = localMap;
            setOfflineTicketsByQrToken(localMap);
        } else {
            offlineTicketsByQrTokenRef.current = new Map();
            setOfflineTicketsByQrToken(new Map());
        }

        const localPendingScans = readPendingScansFromStorage(eventId);
        if (localPendingScans.length > 0) {
            pendingScanSyncQueueRef.current = localPendingScans;
            setPendingScanSyncQueue(localPendingScans);
        } else {
            pendingScanSyncQueueRef.current = [];
            setPendingScanSyncQueue([]);
        }

        void preloadOfflineTicketsMap();
    }, [eventId, preloadOfflineTicketsMap]);

    useEffect(() => {
        void flushPendingScans();

        const intervalId = setInterval(() => {
            void flushPendingScans();
        }, OFFLINE_SCAN_SYNC_INTERVAL_MS);

        const handleBackOnline = () => {
            void flushPendingScans();
        };

        window.addEventListener('online', handleBackOnline);

        return () => {
            clearInterval(intervalId);
            window.removeEventListener('online', handleBackOnline);
        };
    }, [flushPendingScans]);

    useEffect(() => {
        if (qrScannerOpen) {
            startQrScanner();
        } else {
            clearQrAutoResumeTimeout();
        }

        return () => {
            stopQrScanner();
        };
    }, [clearQrAutoResumeTimeout, qrScannerOpen, scannerPaused, startQrScanner, stopQrScanner]);

    useEffect(() => {
        return () => {
            clearQrAutoResumeTimeout();
        };
    }, [clearQrAutoResumeTimeout]);

    return (
        <HeaderFooterLayout>
            <div className={styles.container}>
                <div className={styles.section}>
                    <div className={styles.sectionInner}>
                        <Breadcrumb items={[{label: 'Dashboard', to: '/'}, {label: event?.name || 'Gestione evento'}]} />
                        <header className={styles.managerHeader}>
                            <div><p className={styles.eyebrow}>Gestione evento</p><h1>{loadingEvent ? 'Caricamento…' : (event?.name || 'Event Manager')}</h1><p>Controlla i biglietti, registra gli ingressi e consulta le attività dei partecipanti.</p></div>
                            <Button style={{borderRadius: 8, padding: '.65rem 1rem', fontSize: '.75rem'}} onClick={openQrScanner}><QrCodeIcon /> Scansiona QR</Button>
                        </header>

                        <div className={[styles.pageSection, styles.managementSection].join(' ')}>
                            <h2 className={styles.pageSectionTitle}>Riepilogo ingressi</h2>

                            {offlineDownload.status !== 'idle' &&
                                <div className={styles.downloadPanel} data-status={offlineDownload.status}>
                                    <div className={styles.downloadHeading} role="status" aria-live="polite">
                                        <strong>{offlineDownload.status === 'success'
                                            ? 'Biglietti pronti per la scansione offline'
                                            : offlineDownload.status === 'error'
                                                ? 'Download offline non completato'
                                                : 'Scaricamento biglietti sul dispositivo…'}</strong>
                                        <span>{formatNumber(offlineDownload.downloaded)}{offlineDownload.total !== null ? ` / ${formatNumber(offlineDownload.total)}` : ''} biglietti</span>
                                    </div>
                                    {loadingOfflineTicketsPreload &&
                                        <progress className={styles.downloadProgress} aria-label="Scaricamento biglietti offline"
                                            max={offlineDownload.total || 1}
                                            value={offlineDownload.total ? Math.min(offlineDownload.downloaded, offlineDownload.total) : undefined} />
                                    }
                                    {offlineDownload.status === 'success' && <p>Download completato e salvato su questo dispositivo.</p>}
                                    {offlineDownload.status === 'error' && <>
                                        <p role="alert">{offlineDownload.message}</p>
                                        <Button onClick={preloadOfflineTicketsMap}>Riprova download</Button>
                                    </>}
                                </div>
                            }

                            <div className={styles.managementTopCards}>
                                <div className={styles.managementStatsCards}>
                                    <Card appearance="modern" title="Biglietti venduti">
                                        <MetricStatBlock
                                            title=""
                                            actualValue={formatNumber(soldTotal)}
                                            loading={loadingTickets && soldTotal === 0}
                                            containerClassName={styles.analytic}
                                            titleClassName={styles.analyticTitle}
                                            actualClassName={styles.actualAnalytic}
                                            previousClassName={styles.previousAnalytic}
                                            skeletonClassName={styles.analyticSkeleton}
                                        />
                                    </Card>

                                    <Card appearance="modern" title="Biglietti scansionati">
                                        <MetricStatBlock
                                            title=""
                                            actualValue={formatNumber(scannedTotal)}
                                            loading={loadingScannedTotal && scannedTotal === 0}
                                            containerClassName={styles.analytic}
                                            titleClassName={styles.analyticTitle}
                                            actualClassName={styles.actualAnalytic}
                                            previousClassName={styles.previousAnalytic}
                                            skeletonClassName={styles.analyticSkeleton}
                                        />
                                    </Card>
                                </div>

                                <button type="button" className={styles.scannerCard} onClick={openQrScanner}>
                                    <QrCodeIcon /><span><strong>Controllo accessi</strong><span>Apri la fotocamera e inquadra il QR del biglietto.</span></span><span aria-hidden="true">→</span>
                                </button>
                            </div>

                            <Card appearance="modern" integratedTable title="Biglietti" actions={<span className={styles.resultCount}>{loadingTickets ? 'Caricamento…' : `${filteredTotal} risultati`}</span>}>
                                <div className={styles.search}>
                                    <TextInput appearance="modern"
                                        type="search"
                                        placeholder="Cerca per nome o email"
                                        style={{ width: '100%' }}
                                        onKeyUp={(value) => {
                                            setPage(0);
                                            setQ(value);
                                        }}
                                    />
                                </div>
                                <div className={styles.ticketsActions}>

                                    {(syncingPendingScans || pendingScanSyncQueue.length > 0) &&
                                        <div className={styles.sectionLabel}>
                                            {syncingPendingScans
                                                ? `Sincronizzazione scansioni (${pendingScanSyncQueue.length} in coda)`
                                                : `Scansioni offline in coda: ${pendingScanSyncQueue.length}`}
                                        </div>
                                    }
                                </div>

                                <Table integrated
                                    loading={loadingTickets}
                                    loadingN={25}
                                    onRowClick={(ticket) => {
                                        setManualScanError('');
                                        setManualScanSuccess('');
                                        setSelectedTicket(ticket);
                                    }}
                                    style={{ borderRadius: 0 }}
                                    columns={[
                                        { key: 'id', label: 'ID', type: 'number' },
                                        { key: 'user_name', label: 'NOME', type: 'string' },
                                        { key: 'user_email', label: 'EMAIL', type: 'string' },
                                        { key: 'ticket_type_name', label: 'TIPOLOGIA BIGLIETTO', type: 'string' },
                                        { key: 'activities_count', label: 'ATTIVITA', type: 'custom' },
                                        { key: 'status', label: 'STATO', type: 'custom' },
                                        { key: 'created_at', label: 'DATA ACQUISTO', type: 'datetime' },
                                        { key: 'last_scan', label: 'Ultima scansione', type: 'custom' },
                                    ]}
                                    elements={tickets.map(ticket => ({
                                        ...ticket,
                                        user_name: ticket.user?.name + ' ' + ticket.user?.surname || '—',
                                        user_email: ticket.user?.email || '—',
                                        ticket_type_name: ticket.type?.name || '—',
                                        activities_count: (() => {
                                            const count = Array.isArray(ticket?.activity_bookings) ? ticket.activity_bookings.length : 0;
                                            return {
                                                value: count,
                                                type: 'number',
                                                component: <span>{count}</span>
                                            };
                                        })(),
                                        status: { value: ticket.status, component: getTicketStatusBadge(ticket.status), type: 'string' },
                                        last_scan: (() => {
                                            const latestScan = getLatestScan(ticket);
                                            if (!latestScan) {
                                                return { value: 0, type: 'string', component: <span>—</span> };
                                            }

                                            const dateValue = latestScan.scanned_at
                                                ? `${formatDateV2(latestScan.scanned_at, { day: '2-digit', month: '2-digit', year: 'numeric' })} ${formatTimeV2(latestScan.scanned_at)}`
                                                : '—';
                                            const adminName = latestScan.admin_name || '—';

                                            return {
                                                value: latestScan.scanned_at || '',
                                                type: 'string',
                                                component: (
                                                    <div className={styles.lastScanCell}>
                                                        <div className={styles.lastScanDate}>{dateValue}</div>
                                                        <div className={styles.lastScanAdmin}>{adminName}</div>
                                                    </div>
                                                )
                                            };
                                        })(),
                                        created_at: ticket.created_at
                                    }))}
                                    defaultSort={{ key: 'created_at', direction: 'desc' }}
                                />

                                {filteredTotal > tickets.length &&
                                    <div className={styles.pagination}><Pagination group={width > 540 ? 5 : 0} total={filteredTotal} per={per} page={page} onChange={p => setPage(p)} loading={loadingTickets} /></div>
                                }
                            </Card>
                        </div>
                    </div>
                </div>
            </div>

            {qrScannerOpen &&
                <div className={styles.qrScannerOverlay}>
                    <div className={styles.qrScannerHeader}>
                        <div className={styles.scannerTitle}>Scanner QR Biglietti</div>
                        <div className={styles.qrScannerHeaderActions}>
                            {videoInputDevices.length > 1 &&
                                <select
                                    className={styles.qrCameraSelect}
                                    value={selectedCameraId}
                                    onChange={(event) => {
                                        setSelectedCameraId(event.target.value);
                                        setScannerError('');
                                        setScannerSuccess('');
                                        setScannerPaused(false);
                                    }}
                                    aria-label='Seleziona fotocamera'
                                >
                                    {videoInputDevices.map((device, index) => (
                                        <option key={device.deviceId || index} value={device.deviceId}>
                                            {device.label?.trim() || `Fotocamera ${index + 1}`}
                                        </option>
                                    ))}
                                </select>
                            }
                            {scannerPaused &&
                                <Button appearance='subtle' onClick={() => {
                                    clearQrAutoResumeTimeout();
                                    setScannerError('');
                                    setScannerSuccess('');
                                    setScannerPaused(false);
                                }}>
                                    Scansiona altro
                                </Button>
                            }
                            <Button appearance='subtle' onClick={() => {
                                clearQrAutoResumeTimeout();
                                setQrScannerOpen(false);
                                setScannerPaused(false);
                                setScannerError('');
                                setScannerSuccess('');
                            }} title='Chiudi scanner' aria-label='Chiudi scanner'>
                                <CloseIcon width={18} height={18} />
                            </Button>
                        </div>
                    </div>

                    <div className={styles.qrScannerContent}>
                        <div className={styles.qrScannerCameraPanel}>
                            <video ref={qrVideoRef} className={styles.qrScannerVideo} autoPlay muted playsInline />
                            {(startingQrScanner || scanningTicket) &&
                                <div className={styles.qrScannerStatus}>Scansione in corso...</div>
                            }
                            {scannerError &&
                                <div className={styles.qrScannerError}>{scannerError}</div>
                            }
                            {!scannerError && scannerSuccess &&
                                <div className={styles.qrScannerSuccess}>{scannerSuccess}</div>
                            }
                            {!scannerError && !startingQrScanner && !scanningTicket && !scannerPaused &&
                                <div className={styles.qrScannerHint}>Inquadra il QR code del biglietto</div>
                            }
                        </div>

                        <div className={styles.qrScannerResultPanel}>
                            <div className={styles.sectionLabel}>Ultima scansione</div>

                            {!scannedTicket &&
                                <div className={styles.qrScannerEmpty}>Nessun ticket scansionato</div>
                            }

                            {scannedTicket &&
                                <>
                                    <div className={styles.qrScannerTicketInfo}>
                                        <div><strong>Codice:</strong> {scannedTicketDetails?.qr_token || lastScannedCode || '—'}</div>
                                        <div><strong>Intestatario:</strong> {scannedTicketDetails?.user ? `${scannedTicketDetails.user.name || ''} ${scannedTicketDetails.user.surname || ''}`.trim() || '—' : '—'}</div>
                                        <div><strong>Tipo biglietto:</strong> {scannedTicketDetails?.type?.name || '—'}</div>
                                    </div>

                                    <div className={styles.sectionLabel}>Attività prenotate</div>
                                    <div className={styles.ticketActivitiesList}>
                                        {getTicketActivityBookings(scannedTicketDetails).length === 0 &&
                                            <div className={styles.qrScannerEmpty}>Nessuna iscrizione ad attivita</div>
                                        }

                                        {getTicketActivityBookings(scannedTicketDetails).map((booking, index) => (
                                            <div className={styles.ticketActivityItem} key={`scan-booking-${booking.id || `${booking?.slot?.id}-${booking?.activity?.id}-${index}`}`}>
                                                <div className={styles.ticketActivityTime}>{getActivityBookingTimeRange(booking)}</div>
                                                <div className={styles.ticketActivityMeta}>{getActivityBookingMeta(booking)}</div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className={styles.sectionLabel}>Storico scansioni</div>
                                    <div className={styles.qrScansList}>
                                        {(Array.isArray(scannedTicket.scans) ? scannedTicket.scans : []).length === 0 &&
                                            <div className={styles.qrScannerEmpty}>Nessuna scansione registrata</div>
                                        }

                                        {(Array.isArray(scannedTicket.scans) ? scannedTicket.scans : []).map((scan, index) => (
                                            <div className={styles.qrScanItem} key={`${scan.admin_id}-${scan.scanned_at}-${index}`}>
                                                <div><strong>Admin:</strong> {scan.admin_name || '—'}</div>
                                                <div><strong>Scansionato il:</strong> {scan.scanned_at ? `${formatDateV2(scan.scanned_at, { day: '2-digit', month: '2-digit', year: 'numeric' })} ${formatTimeV2(scan.scanned_at)}` : '—'}</div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            }
                        </div>
                    </div>
                </div>
            }

            <Dialog appearance="modern"
                style={{ maxWidth: 780 }}
                overflow="auto"
                open={!!selectedTicket}
                title={selectedTicketDetails ? `Biglietto #${selectedTicketDetails.id}` : 'Biglietto'}
                onClose={() => {
                    setSelectedTicket(null);
                    setManualScanError('');
                    setManualScanSuccess('');
                }}
                action={
                    <Button disabled={manualScanLoading || !selectedTicketDetails?.qr_token} onClick={markTicketAsScannedNow}>
                        {manualScanLoading ? 'Scansione…' : 'Registra ingresso'}
                    </Button>
                }
            >
                <div className={styles.ticketTypeDialog}>
                    {manualScanError &&
                        <div className={styles.scanFeedbackError}>{manualScanError}</div>
                    }
                    {!manualScanError && manualScanSuccess &&
                        <div className={styles.scanFeedbackSuccess}>{manualScanSuccess}</div>
                    }

                    <div className={styles.ticketDetailGrid}>
                        <div className={styles.ticketDetailCard}>
                            <div className={styles.ticketDetailLabel}>Codice</div>
                            <div className={styles.ticketDetailValue}>{selectedTicketDetails?.qr_token || '—'}</div>
                        </div>
                        <div className={styles.ticketDetailCard}>
                            <div className={styles.ticketDetailLabel}>Tipo biglietto</div>
                            <div className={styles.ticketDetailValue}>{selectedTicketDetails?.type?.name || '—'}</div>
                        </div>
                        <div className={styles.ticketDetailCard}>
                            <div className={styles.ticketDetailLabel}>Data acquisto</div>
                            <div className={styles.ticketDetailValue}>
                                {selectedTicketDetails?.created_at
                                    ? `${formatDateV2(selectedTicketDetails.created_at, { day: '2-digit', month: '2-digit', year: 'numeric' })} ${formatTimeV2(selectedTicketDetails.created_at)}`
                                    : '—'}
                            </div>
                        </div>
                        <div className={styles.ticketDetailCard}>
                            <div className={styles.ticketDetailLabel}>Stato</div>
                            <div className={styles.ticketDetailValue}>
                                {selectedTicketDetails?.status ? getTicketStatusBadge(selectedTicketDetails.status) : '—'}
                            </div>
                        </div>
                        <div className={`${styles.ticketDetailCard} ${styles.ticketDetailCardWide}`}>
                            <div className={styles.ticketDetailLabel}>Intestatario</div>
                            <div className={styles.ticketDetailValue}>{selectedTicketDetails?.user ? `${selectedTicketDetails.user.name || ''} ${selectedTicketDetails.user.surname || ''}`.trim() || '—' : '—'}</div>
                        </div>
                    </div>

                    <div className={styles.sectionLabel}>Attività prenotate</div>
                    <div className={styles.ticketScansSection}>
                        <div className={styles.ticketActivitiesList}>
                            {getTicketActivityBookings(selectedTicketDetails).length === 0 &&
                                <div className={styles.qrScannerEmpty}>Nessuna iscrizione ad attivita</div>
                            }

                            {getTicketActivityBookings(selectedTicketDetails).map((booking, index) => (
                                <div className={styles.ticketActivityItem} key={`detail-booking-${booking.id || `${booking?.slot?.id}-${booking?.activity?.id}-${index}`}`}>
                                    <div className={styles.ticketActivityTime}>{getActivityBookingTimeRange(booking)}</div>
                                    <div className={styles.ticketActivityMeta}>{getActivityBookingMeta(booking)}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={styles.sectionLabel}>Storico scansioni</div>
                    <div className={styles.ticketScansSection}>
                        <div className={styles.qrScansList}>
                            {(Array.isArray(selectedTicketDetails?.scans) ? selectedTicketDetails.scans : []).length === 0 &&
                                <div className={styles.qrScannerEmpty}>Nessuna scansione registrata</div>
                            }

                            {(Array.isArray(selectedTicketDetails?.scans) ? selectedTicketDetails.scans : []).map((scan, index) => (
                                <div className={styles.qrScanItem} key={`${scan.admin_id}-${scan.scanned_at}-${index}`}>
                                    <div><strong>Admin:</strong> {scan.admin_name || '—'}</div>
                                    <div><strong>Scansionato il:</strong> {scan.scanned_at ? `${formatDateV2(scan.scanned_at, { day: '2-digit', month: '2-digit', year: 'numeric' })} ${formatTimeV2(scan.scanned_at)}` : '—'}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </Dialog>

            <AlertDialog
                open={manualScanConfirm.open}
                title={'Confermare scansione?'}
                text={'Confermi la scansione manuale del biglietto selezionato?'}
                actions={[
                    {
                        label: 'CONFERMA',
                        onClick: async () => {
                            setManualScanConfirm({ open: false, code: null });
                            await runManualTicketScan();
                        }
                    }
                ]}
                onClose={() => setManualScanConfirm({ open: false, code: null })}
            />
        </HeaderFooterLayout>
    );
};

export default EventManager;
